import Enumerable from "linq";
import { ObjectUtility } from "../../../base/utilities";

export class FormatSourceComplementor {
  public toComplementedSource(source: any): any {
    // 比較時に違いとして出る物を補完して差異にならないようにする.
    if (!source) {
      return null;
    }
    const clone = ObjectUtility.copyDeeply(source);
    if (JSON.stringify(source) !== JSON.stringify(clone)) {
      console.error("clone結果が不正です.");
    }
    this.complementNotComparedAttribute(clone.formatInfo, "version");
    this.complementDelegatedAttribute(clone.formatInfo, "companyName");
    this.complementDelegatedAttribute(clone.formatInfo, "sort");
    clone.sections = this.complementArrayToCompare(
      clone.sections,
      s => this.toComplementedSection(s));
    return clone;
  }

  private complementFloatTypeAttribute(source: any, name: string): void {
    // float型の属性はjavascriptの浮動小数点数の低精度のせいで若干ずれる可能性あり.16桁でtrimする.
    this.complementAttribute(
      source,
      name,
      (source[name] && (16 < source[name].length)) ? source[name].substring(0, 16) : source[name]);
  }

  private complementNotComparedAttribute(source: any, name: string): void {
    // バージョン等書き戻しただけで更新されるため比較対象外とする属性を差分出ないよう補完する.
    this.complementAttribute(source, name, "比較対象外");
  }

  private complementDelegatedAttribute(source: any, name: string): void {
    this.complementAttribute(source, name, "バックエンドが補完するので比較対象外");
  }

  private complementUnsupporableAttribute(source: any, name: string): void {
    this.complementAttribute(source, name, "非対応版があるので比較対象外");
  }

  private complementGabageAttribute(source: any, name: string): void {
    this.complementAttribute(source, name, "通常設定されないゴミ属性と思われるので比較対象外");
  }

  private complementNumberAttribute(source: any, name: string): void {
    this.complementAttribute(source, name, Number(source[name]) + "※数値として補完(先頭の冗長な0除去等)");
  }

  private complementAttribute(source: any, name: string, value: string): void {
    // ない方のオブジェクトにプロパティ追加してもある方と同じ位置に入らない.
    // 一旦削除(undefined代入)してから入れても、ある方は順番が維持されてる模様.
    // 仕方ないので元のプロパティは削除して別のプロパティ名で補完する.
    source[name] = undefined; // 順番を合わせられないので削除.
    source["※補完済)" + name] = value;
  }

  private complementAttributeOrderOnly(source: any, name: string): void {
    // 並び順を合わせるために無理な実装が必要な場合は合わせないで補完する.
    source["※位置だけ補完済)" + name] = source[name];
    source[name] = undefined; // 順番を合わせられないので削除.
  }

  private toComplementedSection(source: any): any {
    this.complementNotComparedAttribute(source.contents.printformat, "@version");
    this.complementNotComparedAttribute(source.contents.printformat, "@app-version");
    this.complementNotComparedAttribute(source.contents.printformat, "@createdate");
    this.complementUnsupporableAttribute(source.contents.printformat, "@maxobj-id");
    this.complementFloatTypeAttribute(source.contents.printformat.page, "@paperwidth");
    this.complementFloatTypeAttribute(source.contents.printformat.page, "@paperheight");
    this.complementFloatTypeAttribute(source.contents.printformat.page, "@imagewidth");
    this.complementFloatTypeAttribute(source.contents.printformat.page, "@imageheight");
    this.complementArrayToCompare(
      source.contents.printformat.page.objects,
      s => this.toComplementedObject(s));
    return source;
  }

  private toComplementedObject(source: any): any {
    this.complementObjectTitleAttribute(source);
    this.complementObjectDataValidationAttribute(source);
    this.complementObjectVisibleAttribute(source);
    this.complementObjectPtProfileOptionAttribute(source);
    this.complementObjectMuscularStrengthAttribute(source);
    this.complementObjectCalculateItemAttribute(source);
    this.complementRelationalAttribute(source);
    return source;
  }

  private complementObjectTitleAttribute(source: any): void {
    // @titleはHIS情報文字列では通常undefinedだが古い版は空文字列の場合もあるので補完.
    if (this.isHisInformationObject(source) && ((source["@title"] === "") || (source["@title"] === undefined))) {
      this.complementAttribute(source, "@title", "undefined or 空文字列(HIS情報文字列)");
    }
  }

  private isHisInformationObject(source: any): boolean {
    return this.isObject(source, "text", "date")
        && !!source["@name"]
        && !source["@source-file"]
        && (source["@name"] !== "指定医番号");
  }

  private isObject(source: any, ...elementNames: string[]): boolean {
    return Enumerable.from(elementNames).any(n => source["@element-name"] === n);
  }

  private complementObjectDataValidationAttribute(source: any): void {
    // @data-validationは通常Noneだが古い版はundefinedの場合もあるので補完.
    if ((source["@data-validation"] === undefined) || (source["@data-validation"] === "None")) {
      this.complementAttribute(source, "@data-validation", "None or undefined");
    }
  }

  private complementObjectVisibleAttribute(source: any): void {
    // @visibleは途中から年号bit(0x1<<7)が追加になったので古い版は年号bitが立ってないので補完.
    const isDate = !source["@time-format"];
    const hasEra = (source["@format"] === "3") || (source["@format"] === "6") || (source["@format"] === "13");
    if (isDate && hasEra) {
      this.complementAttribute(source, "@visible", (parseInt(source["@visible"], 10) | 128).toString());
    }
  }

  private complementObjectPtProfileOptionAttribute(source: any): void {
    // @ptprofile-optionは元々checkとcircleselectで仕様が違ったが最新版では共通.古い版だとNoneはundefinedなので補完.
    if (this.isObject(source, "check") && ((source["@ptprofile-option"] === undefined) || (source["@ptprofile-option"] === "0"))) {
      this.complementAttribute(source, "@ptprofile-option", "0 or undefined");
    }
  }

  private complementObjectMuscularStrengthAttribute(source: any): void {
    // @muscular-strengthは折れ線グラフでも固定値1が指定されるが古い版ではundefinedの場合もあるので補完.
    if ((source["@type"] === "2") && ((source["@muscular-strength"] === undefined) || (source["@muscular-strength"] === "1"))) {
      this.complementAttribute(source, "@muscular-strength", "1 or undefined(折れ線グラフ)");
    }
  }

  private complementObjectCalculateItemAttribute(source: any): void {
    this.complementArrayToCompare(
      source.calculateitem,
      s => this.toComplementedCalculateItem(s, source["@form-type"]));
  }

  private toComplementedCalculateItem(source: any, formType: string): any {
    this.complementedCalculateItemItemTypeAttribute(source, formType);
  }

  private complementedCalculateItemItemTypeAttribute(source: any, formType: string): void {
    // 以下の属性の並び順を合わせるのは難しいので値はそのまま並び順だけ補正する.
    this.complementAttributeOrderOnly(source, "@operator");
    if ((formType === "1") && (source["@item-type"] !== "1")) {
      // 計算式の連携オブジェクト以外に指定されてる事がある.(ゴミ？改ざん？)比較対象から外す.
      this.complementGabageAttribute(source, "@empty-value");
    } else {
      this.complementNumberAttribute(source, "@empty-value");
    }
    if ((formType === "1") && ((source["@item-type"] !== "1") || (source["@type"] !== "calculate"))) {
      // 計算式のcalculate連携オブジェクト以外に指定されてる事がある.(ゴミ？改ざん？)比較対象から外す.
      this.complementGabageAttribute(source, "@option");
    } else {
      this.complementAttributeOrderOnly(source, "@option");
    }
    if ((formType === "1") && (source["@item-type"] === "2")) {
      this.complementNumberAttribute(source, "@value");
    }
  }

  private complementRelationalAttribute(source: any): void {
    this.complementArrayToCompare(
      source.relational,
      s => this.toComplementedRelational(s));
    this.complementEmptyRelationalAction(source, "text-paste");
    this.complementEmptyRelationalAction(source, "calculate");
    this.complementEmptyRelationalAction(source, "user-define");
    this.complementEmptyRelationalAction(source, "object-link");
    this.complementEmptyRelationalAction(source, "parent-alert");
    this.complementEmptyRelationalAction(source, "name-history");
    this.complementEmptyRelationalAction(source, "script-link-output");
    this.complementEmptyRelationalAction(source, "script-link-input");
    this.complementEmptyRelationalAction(source, "base-date");
    this.complementEmptyRelationalAction(source, "window-type");
    this.complementEmptyRelationalAction(source, "jump-link");
    this.complementEmptyRelationalAction(source, "copies-text");
    this.complementEmptyRelationalAction(source, "designate-doctor");
  }

  private toComplementedRelational(source: any): any {
    if (!!source) {
      this.complementArrayToCompare(
        source.relationalitem,
        s => this.toComplementedRelationalItem(source["@action"], s));
    }
    return source;
  }

  private toComplementedRelationalItem(action: string, source: any): any {
    this.toComplementedRelationalItemItemTypeAttribute(action, source);
  }

  private toComplementedRelationalItemItemTypeAttribute(action: string, source: any): void {
    if (action === "parent-alert") {
      // parent-alertの@item-typeはtranslatorで補完してる都合上並び順を合わせるのは難しいので値はそのまま並び順だけ補正する.
      this.complementAttributeOrderOnly(source, "@item-type");
    }    
  }

  private complementArrayToCompare<T>(source: T | T[], complement: (s: T) => T): T | T[] {
    return ((source !== undefined) && (source !== null))
      ? (Array.isArray(source) ? Enumerable.from(source).select(complement).toArray() : complement(source))
      : source;
  }

  private complementEmptyRelationalAction(source: any, action: string): void {
    // v1は一度設定したactionを全て削除してもrelationalitemなしのrelationalが残ってしまう模様.
    // 差分として検出されないようrelationalからは抜いて補完済の記録として属性を追加する.
    const count = this.removeRelationalAction(source, s => (s["@action"] === action) && (!s.relationalitem));
    if ((0 < count) || !this.findRelationalAction(source, s => (s["@action"] === action))) {
      source["※補完済)relational[@action='" + action + "']"] = "undefined or empty";
    }
  }

  private removeRelationalAction(source: any, match: (s: any) => boolean): number {
    const relational = source.relational;
    if (!relational) {
      return 0;
    } else if (Array.isArray(relational)) {
      const unmatched = Enumerable.from(relational).where(s => !match(s)).toArray();
      const count = source.relational.length - unmatched.length;
      if (0 < count) {
        if (unmatched.length === 0) {
          source.relational = undefined;
        } else if (unmatched.length === 1) {
          source.relational = unmatched[0];
        } else {
          source.relational = unmatched;
        }
      }
      return count;
    } else {
      const isMatched = match(relational);
      if (isMatched) {
        source.relational = undefined;
        return 1;
      } else {
        return 0;
      }
    }
  }

  private findRelationalAction(source: any, match: (s: any) => boolean): number {
    const relational = source.relational;
    if (!relational) {
      return null;
    } else if (Array.isArray(relational)) {
      return Enumerable.from(relational).firstOrDefault(s => match(s));
    } else {
      return match(relational) ? relational : null;
    }
  }
}
